import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, Check, Smartphone } from "lucide-react";
import { api, downloadFile, errMsg, local } from "@/lib/api";
import { saveLockScreen } from "@/lib/lockscreen";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DEFAULT_SKILLS } from "@/data/coping";

const useSkillOptions = () => useMemo(() => {
  const all = [...DEFAULT_SKILLS, ...local.get("coping_custom", [])];
  const favIds = local.get("coping_favorites", []);
  const favs = all.filter((s) => favIds.includes(s.id));
  return { favs, rest: all.filter((s) => !favIds.includes(s.id)) };
}, []);

const DEFAULT_SKILLS_FILL = ["Box Breathing", "5-4-3-2-1 Senses", "Call someone I trust"];

const SkillButton = ({ s, on, onClick }) => (
  <button onClick={onClick} data-testid={`wallet-skill-${s.id}`}
    className={`text-left px-3 py-2 rounded-xl border-2 text-sm flex items-center gap-2 transition-colors ${on ? "border-mm-primary bg-mm-soft" : "border-mm-line"}`}>
    <span>{s.icon}</span><span className="flex-1">{s.name}</span>{on && <Check size={14} className="text-mm-primary" />}
  </button>
);

export const WalletCardDialog = () => {
  const { favs, rest } = useSkillOptions();
  const [picked, setPicked] = useState(() => favs.slice(0, 3).map((s) => s.name));
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const { user } = useAuth();

  const saveImage = async () => {
    setImgBusy(true);
    try {
      const { data } = await api.get("/safety/full-plan");
      const skills = [...picked, ...DEFAULT_SKILLS_FILL.filter((d) => !picked.includes(d))].slice(0, 3);
      const how = await saveLockScreen({ name: user?.name, skills, contact: data.plan?.trustedContacts?.[0] });
      toast.success(how === "shared" ? "Choose \"Save Image\", then set it as your lock screen" : "Image saved. Set it as your phone's lock screen");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(errMsg(e, "Could not create image"));
    } finally {
      setImgBusy(false);
    }
  };

  const toggle = (name) => setPicked((p) => (p.includes(name) ? p.filter((x) => x !== name) : p.length < 3 ? [...p, name] : p));
  const shown = rest.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, 30);

  const download = async () => {
    setBusy(true);
    try { await downloadFile("/safety/wallet-card/pdf", "wallet-crisis-card.pdf", { skills: picked }); toast.success("Wallet card downloaded — print at 100% scale"); }
    catch (e) { toast.error(errMsg(e, "Could not create card")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="mm-btn-ghost" data-testid="safety-wallet-card-button"><CreditCard size={16} /> Wallet crisis card</button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="wallet-card-dialog">
        <DialogHeader><DialogTitle>Pocket crisis card</DialogTitle></DialogHeader>
        <p className="text-sm text-mm-muted">Credit-card sized: crisis numbers and your first trusted contact on the front, three go-to coping skills on the back. Pick up to 3 ({picked.length}/3).</p>
        {favs.length > 0 && (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider text-mm-muted mt-2">Your favorites</div>
            <div className="grid sm:grid-cols-2 gap-2">{favs.map((s) => <SkillButton key={s.id} s={s} on={picked.includes(s.name)} onClick={() => toggle(s.name)} />)}</div>
          </>
        )}
        <input className="mm-input !py-2 mt-2" placeholder="Search all skills…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="wallet-skill-search" />
        <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1">
          {shown.map((s) => <SkillButton key={s.id} s={s} on={picked.includes(s.name)} onClick={() => toggle(s.name)} />)}
        </div>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          <button className="mm-btn" onClick={download} disabled={busy} data-testid="wallet-card-download">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Printable card
          </button>
          <button className="mm-btn-ghost" onClick={saveImage} disabled={imgBusy} data-testid="wallet-card-lockscreen">
            {imgBusy ? <Loader2 size={16} className="animate-spin" /> : <Smartphone size={16} />} Lock-screen image
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
